import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { verifyOtp as verifyOtpAction } from 'modules/state-mgmt/user';
import { fetchWorkerDetails, setOnboarding } from 'modules/state-mgmt/onboarding';
import useVerifyAdminContext from 'hooks/useVerifyAdminContext';
import useResumeProjectOnboarding from 'hooks/useResumeProjectOnboarding';
import { WorkerInvitationStatus } from 'models/general';
import { prettifyErrorResponse } from 'utils';
import { ROUTES } from 'constants/routes';

interface IUseVerifyOtp {
  onFailure?: (error: unknown) => void;
}

/**
 * Verifies a one-time password for a worker signing in inside an admin session (CH-4506).
 *
 * No worker session is created — the admin's session keeps authorizing every API call.
 * "Signing the worker in" means loading them into the onboarding slice, which is what the
 * password-based path in `Login/hooks.ts` does too.
 */
export const useVerifyOtp = ({ onFailure }: IUseVerifyOtp) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const languageCode = useAppSelector(state => state.app?.languageCode);
  const { verifyAndRestore } = useVerifyAdminContext();
  // CH-4652 — same resume the password sign-in runs, so a worker who resets their password
  // mid-flow still lands on the project they were being onboarded onto.
  const { isResuming, resumeProjectOnboarding } = useResumeProjectOnboarding({
    onSettled: () => setIsLoading(false),
  });

  const verifyOtp = async (pinCode: string, email?: string, mobilePhoneNumber?: string) => {
    setIsLoading(true);

    try {
      const { userId, workerId } = await dispatch(
        verifyOtpAction({ pinCode, email, mobilePhoneNumber, languageCode }),
      ).unwrap();

      // `fetchWorkerDetails` only spreads the worker payload into the slice, and it does not
      // necessarily carry `userId`. Seed it explicitly — MyProfile derives its admin-vs-worker
      // context from `onboarding.userId`, and the contacts thunks resolve their endpoint from it.
      //
      // `isOtpVerifiedLogin` marks this worker as already-onboarded so the route guard keeps their
      // worker-facing pages reachable even if they never set a password (CH-4515). It is cleared by
      // `resetOnboardingState` when a different worker or project is started.
      dispatch(setOnboarding({ userId, id: workerId, isOtpVerifiedLogin: true }));

      const worker = await dispatch(fetchWorkerDetails({ workerId, languageCode })).unwrap();

      // `invitationStatus === ACCEPTED` is the app's existing signal for "password is set"
      // (see routeGuard.ts `isPasswordCreated`). Anything else means this worker was onboarded
      // without ever creating a password, so send them to Create Password first (CH-4515).
      const isPasswordCreated = worker?.invitationStatus === WorkerInvitationStatus.ACCEPTED;

      if (!isPasswordCreated) {
        // Creating the password comes first. The CH-4652 marker is deliberately left in place so
        // that `MyProfile`'s Create Password step runs the resume once the password exists — the
        // worker still owes a password before any project work makes sense.
        navigate(`${ROUTES.MY_PROFILE.path}?tab=password`);
        return;
      }

      // Restore the admin's saved project context before landing on Project Listing, so the
      // list renders with data instead of briefly empty.
      await verifyAndRestore();

      // CH-4652 — if the duplicate-worker check sent them here, attach them to the project chosen
      // beforehand and continue onboarding rather than dropping them on My Projects.
      if (await resumeProjectOnboarding(ROUTES.PROJECT_LIST.path)) return;

      navigate(ROUTES.PROJECT_LIST.path);
    } catch (error) {
      onFailure?.(error);
      const prettyError = prettifyErrorResponse(error);
      prettyError && enqueueSnackbar(prettyError, { variant: 'error' });
    } finally {
      // Cleared only once the whole sequence is done — verify, worker refetch and the admin
      // context restore — so the button stays busy for all of it.
      setIsLoading(false);
    }
  };

  return { isLoading, isResuming, verifyOtp };
};
